import { useRef, useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { Button } from "@/components/Button";
import { TextField } from "@/components/Field";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/**
 * Sign in.
 *
 * One failure message for every reason. This console is a more attractive
 * target than anything else in the product, and telling somebody probing it
 * which staff addresses exist is not a trade worth making for the convenience
 * of a colleague who mistyped their own email.
 */
export function LoginPage() {
  const t = useT();
  const { status, signIn, challenge, sendChallengeCode, completeChallenge, cancelChallenge } =
    useAuth();
  const { showError } = useToast();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const recaptcha = useRef<HTMLDivElement>(null);

  if (status !== "signedOut" && status !== "loading") {
    /*
      Back where they were, unless where they were is a gate.

      `/enrol` and `/login` are both places somebody is sent, never places they
      meant to go. Returning an enrolled admin to the enrolment page — which is
      exactly what happens on the first sign-in after enrolling — would ask them
      to do the thing they have just done.
    */
    const from = (location.state as { from?: string } | null)?.from;
    const gate = from === "/login" || from === "/enrol";
    return <Navigate to={from && !gate ? from : "/"} replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await signIn(email, password);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  /*
    The second step.

    Every admin who has done as this console asked arrives here on every
    sign-in, so it is the ordinary path rather than an exception. Sending the
    code is a separate press from submitting the password: an SMS that goes out
    on page load is an SMS somebody gets when they mistyped their password.
  */
  const onSend = async () => {
    if (!recaptcha.current) return;
    setBusy(true);
    try {
      await sendChallengeCode(recaptcha.current);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await completeChallenge(code);
      setCode("");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-paper-warm">
      <div className="flex justify-end p-4">
        <LanguageSwitcher />
      </div>

      <main className="flex flex-1 items-start justify-center px-4 pb-24 pt-8">
        <div className="card w-full max-w-sm p-8">
          <h1 className="font-display text-2xl text-ink">{t("app.name")}</h1>
          <p className="mt-1 text-sm text-ink-muted">{t("app.admin")}</p>
          <p className="mt-4 text-sm text-ink-soft">{t("auth.staffOnly")}</p>

          {challenge ? (
            <div className="mt-8">
              <h2 className="font-display text-lg text-ink">{t("auth.mfa.title")}</h2>
              <p className="mt-2 text-sm text-ink-soft">
                {t("auth.mfa.body", { hint: challenge.hint })}
              </p>

              {challenge.sent ? (
                <form className="mt-6 flex flex-col gap-4" onSubmit={onVerify}>
                  <TextField
                    labelKey="auth.mfa.code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                  />
                  <Button type="submit" busy={busy} className="w-full">
                    {t("auth.mfa.verify")}
                  </Button>
                </form>
              ) : (
                <Button busy={busy} onClick={onSend} className="mt-6 w-full">
                  {t("auth.mfa.send")}
                </Button>
              )}

              <div ref={recaptcha} />

              <button
                type="button"
                onClick={() => {
                  cancelChallenge();
                  setCode("");
                }}
                className="mt-6 text-sm text-ink-muted underline underline-offset-2 hover:text-ink"
              >
                {t("auth.mfa.startOver")}
              </button>
            </div>
          ) : (
          <form className="mt-8 flex flex-col gap-4" onSubmit={onSubmit}>
            <TextField
              labelKey="auth.email"
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <TextField
              labelKey="auth.password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Button type="submit" busy={busy} className="mt-2 w-full">
              {busy ? t("auth.signingIn") : t("auth.signIn")}
            </Button>
          </form>
          )}
        </div>
      </main>
    </div>
  );
}
